import React, { FC, ReactNode, Suspense } from 'react';
import SectionLayout from 'components/shared/SectionLayout';
import InitializationStatus from 'app/modules/insights/components/InitializationStatus';
import style from 'app/modules/insights/components/mainSections/MainSections.module.scss';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';
import routes from 'constants/routes';
import { useRedirectRestrictedViewToDefault } from '../hooks/useRedirectRestrictedViewToDefault';
import { useInsightsContainer } from 'components/insights/context';
import InsightsLayoutTobBar from 'app/modules/insights/components/InsightsLayoutTobBar';
import { getInsightsSectionNameByRoute } from 'app/modules/insights/utils/getInsightsSectionNameByRoute';
import { Alert } from '@kontentino/ui';
import InsightsDemoView from 'app/modules/insights/components/demoViews/InsightsDemoView';
import { ActionButton } from 'app/components/actions/ActionButton';
import { useActions } from 'app/hooks/useActions';
import AnalyticsViewSwitcher from 'app/modules/insights/components/AnalyticsViewSwitcher';
import { Trans, useTranslation } from 'react-i18next';

type Props = {
  children: ReactNode;
};

const InsightsSection: FC<Props> = ({ children }) => {
  const location = useLocation();
  const insightsState = useInsightsContainer();
  const { profileActions } = useActions('profile-selector');
  const { t } = useTranslation();

  useRedirectRestrictedViewToDefault(
    insightsState.activeState.page?._type,
    routes.INSIGHTS_POSTS_PERFORMANCE,
  );

  function renderMain() {
    if (insightsState.arePagesLoading) {
      return <InitializationStatus status="loading" />;
    }

    if (insightsState.pages.length === 0) {
      return (
        <section className={style.mainSections}>
          <main
            className={style.mainRoute}
            id="insights-main-route"
            data-cy={getSectionDataCy(location.pathname)}
          >
            <div className="tw-mb-8">
              <Alert title={`💡 ${t('insights.viewingDemoData')}`}>
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-6">
                  <Trans
                    i18nKey="insights.demoDataDescription"
                    components={{ strong: <strong /> }}
                  />
                  <ActionButton action={profileActions.add} variant="primary" />
                </div>
              </Alert>
            </div>
            <InsightsDemoView
              view={getInsightsSectionNameByRoute(location.pathname)}
            />
          </main>
        </section>
      );
    }

    const activePage = insightsState.activeState.page;

    if (!activePage) {
      return (
        <InitializationStatus
          status="error"
          message={{
            main: t('insights.noProfileSelected'),
            secondary: t('insights.selectProfileHint'),
          }}
        />
      );
    }

    return (
      <section className={style.mainSections}>
        <main
          className={style.mainRoute}
          id="insights-main-route"
          data-cy={getSectionDataCy(location.pathname)}
        >
          <Suspense fallback={<Skeleton width="100%" height="80vh" />}>
            {children}
          </Suspense>
        </main>
      </section>
    );
  }

  return (
    <SectionLayout
      header={
        <>
          <InsightsLayoutTobBar />
          <div className="tw-mb-2 tw-mt-6 tw-flex tw-w-full tw-justify-end tw-px-6">
            <AnalyticsViewSwitcher />
          </div>
        </>
      }
      mainProps={{ style: { padding: 0, overflow: 'initial' } }}
      main={renderMain()}
      dataCy="insights-main"
    />
  );
};

function getSectionDataCy(route: string) {
  return `insights-${getInsightsSectionNameByRoute(route)}-module`;
}

export default InsightsSection;
